import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../utils/axiosInstance";
import { useLoading } from "../components/Loading/LoadingProvider";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useConfirmDialog } from "../utils/ConfirmDialog/ConfirmDialogProvider";
import InputText from "../components/InputText";
import { useAuth } from "../utils/AuthProvider";
import { ilceler, iller } from "../assets/common";
import StateSelector from "../components/StateSelector";
import WarningArea from "../components/WarningArea";


const Profile = () => {
    const { user, logout } = useAuth();

    const { username } = useParams();
    const navigate = useNavigate();
    const { setLoading } = useLoading();
    const [userData, setUserData] = useState();
    const [formChanged, setFormChanged] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const toast = useRef(null);
    const { showDialog } = useConfirmDialog();

    const profileFormik = useFormik({
        initialValues: {
            phoneNumber1: "",
            phoneNumber2: "",
            detailOfResidentialAddress: "",
            countryOfResidentialAddress: null, // Object olarak değiştir
            districtOfResidentialAddress: null, // Object olarak değiştir
            countryOfWorkAddress: null, // Object olarak değiştir
            districtOfWorkAddress: null,
            detailOfWorkAddress: ""
        },
        validationSchema: Yup.object({
            phoneNumber1: Yup.string()
                .test('phone-format', 'Telefon numarası 5 ile başlamalı', function (value) {
                    if (!value) return false;
                    const cleanPhone = value.replace(/[^\d]/g, '');
                    const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
                    return /^5[0-9]{9}$/.test(phoneWithoutCountry);
                })
                .required("Telefon numarası zorunludur"),
            phoneNumber2: Yup.string()
                .test('phone-format-optional', 'Telefon numarası 5 ile başlamalı', function (value) {
                    if (!value || value === '') return true;
                    const cleanPhone = value.replace(/[^\d]/g, '');
                    const phoneWithoutCountry = cleanPhone.startsWith('0') ? cleanPhone.slice(1) : cleanPhone;
                    return /^5[0-9]{9}$/.test(phoneWithoutCountry);
                }),
            detailOfResidentialAddress: Yup.string().required("İkamet adresi zorunludur"),
            countryOfResidentialAddress: Yup.object().nullable().required("İkamet ili zorunludur"), // Object validation
            districtOfResidentialAddress: Yup.object().nullable().required("İkamet ilçesi zorunludur"), // Object validation
            countryOfWorkAddress: Yup.object().nullable().required("İş ili zorunludur"), // Object validation
            districtOfWorkAddress: Yup.object().nullable().required("İş ilçesi zorunludur"), // Object validation
            detailOfWorkAddress: Yup.string().required("İş adresi zorunludur")
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                // Telefon numaralarını temizle
                const cleanPhone1 = values.phoneNumber1.replace(/[^\d]/g, '');
                const phone1 = cleanPhone1.startsWith('0') ? cleanPhone1.slice(1) : cleanPhone1;

                const cleanPhone2 = values.phoneNumber2 ? values.phoneNumber2.replace(/[^\d]/g, '') : null;
                const phone2 = cleanPhone2 ? (cleanPhone2.startsWith('0') ? cleanPhone2.slice(1) : cleanPhone2) : null;

                const updatedValues = {
                    ...values,
                    phoneNumber1: phone1,
                    phoneNumber2: phone2 || "",
                    countryOfResidentialAddress: values.countryOfResidentialAddress?.il,
                    districtOfResidentialAddress: values.districtOfResidentialAddress?.ilce,
                    countryOfWorkAddress: values.countryOfWorkAddress?.il,
                    districtOfWorkAddress: values.districtOfWorkAddress?.ilce,
                };

                const res = await axiosInstance.put(`/api/v1/users/${username}/update-profile`, updatedValues);
                if (res?.status === 200 && res?.data) {
                    setUserData(res.data);
                }

                toast.current.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Profil bilgileriniz başarıyla güncellendi",
                    life: 3000
                });
                setFormChanged(false);
            } catch (error) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "Profil güncellenirken bir hata oluştu",
                    life: 3000
                });
                console.error("Error updating profile:", error);
            } finally {
                setTimeout(() => setLoading(false), 2000);
            }
        }
    });

    // Password form with Formik
    const passwordFormik = useFormik({
        initialValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        },
        validationSchema: Yup.object({
            currentPassword: Yup.string().required("Mevcut şifre zorunludur"),
            newPassword: Yup.string()
                .min(8, "Şifre en az 8 karakter olmalıdır")
                .matches(/[a-z]/, "En az bir küçük harf içermelidir")
                .matches(/[A-Z]/, "En az bir büyük harf içermelidir")
                .matches(/[0-9]/, "En az bir rakam içermelidir")
                .matches(/[^a-zA-Z0-9]/, "En az bir özel karakter içermelidir")
                .required("Yeni şifre zorunludur"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("newPassword")], "Şifreler eşleşmiyor")
                .required("Şifre tekrarı zorunludur")
        }),
        onSubmit: async (values, { resetForm }) => {
            setLoading(true);
            try {
                await axiosInstance.post(`/api/v1/users/${username}/change-password`, {
                    currentPassword: values.currentPassword,
                    newPassword: values.newPassword,
                    confirmPassword: values.confirmPassword
                });

                toast.current.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Şifreniz başarıyla güncellendi. Yeniden giriş yapmanız için yönlendiriliyorsunuz!",
                    life: 3000
                });
                setShowPasswordModal(false);
                resetForm();
                setTimeout(() => logout(), 3000);
            } catch (error) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "Şifre güncellenirken bir hata oluştu",
                    life: 3000
                });
                console.error("Error updating password:", error);
            } finally {
                setTimeout(() => setLoading(false), 2000);
            }
        }
    });

    // Fetch user data
    useEffect(() => {
        const loggedInUsername = user.username;

        if (username !== loggedInUsername) {
            alert("Bu profile erişim yetkiniz yok.");
            return navigate(`/`);
        }

        const getUserData = async () => {
            setLoading(true);
            try {
                const userRes = await axiosInstance.get(`/api/v1/users/${username}`);
                if (userRes?.status === 200 && userRes?.data) {
                    setUserData(userRes.data);

                    // Telefon numaralarını format et
                    const formatPhone = (phone) => {
                        if (!phone) return "";
                        const phoneStr = phone.toString();
                        if (phoneStr.length === 10 && phoneStr.startsWith('5')) {
                            return `0${phoneStr}`;
                        }
                        return phoneStr;
                    };

                    // Set form values
                    profileFormik.setValues({
                        phoneNumber1: formatPhone(userRes.data.phoneNumber1),
                        phoneNumber2: formatPhone(userRes.data.phoneNumber2),
                        detailOfResidentialAddress: userRes.data.detailOfResidentialAddress || "",
                        countryOfResidentialAddress: userRes.data.countryOfResidentialAddress
                            ? iller.find(item => item.il === userRes.data.countryOfResidentialAddress) || null
                            : null,
                        districtOfResidentialAddress: userRes.data.districtOfResidentialAddress
                            ? ilceler.find(item =>
                                item.il === userRes.data.countryOfResidentialAddress &&
                                item.ilce === userRes.data.districtOfResidentialAddress
                            ) || null
                            : null,
                        countryOfWorkAddress: userRes.data.countryOfWorkAddress
                            ? iller.find(item => item.il === userRes.data.countryOfWorkAddress) || null
                            : null,
                        districtOfWorkAddress: userRes.data.districtOfWorkAddress
                            ? ilceler.find(item =>
                                item.il === userRes.data.countryOfWorkAddress &&
                                item.ilce === userRes.data.districtOfWorkAddress
                            ) || null
                            : null,
                        detailOfWorkAddress: userRes.data.detailOfWorkAddress || ""
                    });
                }
            } catch (err) {
                console.error('Kullanıcı verisi alınamadı:', err.response?.data || err.message);
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: "Kullanıcı verisi alınamadı",
                    life: 3000
                });
            } finally {
                setTimeout(() => setLoading(false), 2000);
            }
        };

        getUserData();
    }, [username, navigate, setLoading]);

    // Track form changes
    useEffect(() => {
        if (userData) {
            const formatPhone = (phone) => {
                if (!phone) return "";
                const phoneStr = phone.toString();
                if (phoneStr.length === 10 && phoneStr.startsWith('5')) {
                    return `0${phoneStr}`;
                }
                return phoneStr;
            };

            const hasChanges =
                formatPhone(userData.phoneNumber1) !== profileFormik.values.phoneNumber1 ||
                formatPhone(userData.phoneNumber2) !== profileFormik.values.phoneNumber2 ||
                userData.detailOfResidentialAddress !== profileFormik.values.detailOfResidentialAddress ||
                userData.countryOfResidentialAddress !== profileFormik.values.countryOfResidentialAddress?.il ||
                userData.districtOfResidentialAddress !== profileFormik.values.districtOfResidentialAddress?.ilce ||
                userData.countryOfWorkAddress !== profileFormik.values.countryOfWorkAddress?.il ||
                userData.districtOfWorkAddress !== profileFormik.values.districtOfWorkAddress?.ilce ||
                userData.detailOfWorkAddress !== profileFormik.values.detailOfWorkAddress;

            setFormChanged(hasChanges);
        }
    }, [profileFormik.values, userData]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("tr-TR");
    };

    const handleSubmitClick = () => {
        showDialog({
            message: "Profil bilgilerinizi güncellemek istediğinize emin misiniz?",
            onConfirm: () => {
                profileFormik.submitForm();
            }
        });
    };

    if (!userData) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
            <Toast ref={toast} />

            {/* Password Update Modal */}
            <Dialog
                header="Şifre Güncelleme"
                visible={showPasswordModal}
                onHide={() => setShowPasswordModal(false)}
                className="w-[95vw] sm:w-full sm:max-w-md mx-2 h-fit"
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                footer={
                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 p-2 sm:p-0">
                        <Button
                            type="button"
                            className="border rounded-xl shadow-lg cursor-pointer text-red-700 dark:text-red-400 hover:text-white dark:hover:text-white border-red-700 dark:border-red-500 hover:bg-red-800 dark:hover:bg-red-700 p-2.5 sm:p-2 w-full sm:w-auto text-sm sm:text-base"
                            label="İptal"
                            icon="pi pi-times"
                            onClick={() => {
                                setShowPasswordModal(false);
                                passwordFormik.resetForm();
                            }}
                        />
                        <Button
                            type="button"
                            className="rounded-xl shadow-lg cursor-pointer text-white bg-green-700 dark:bg-green-800 hover:bg-green-800 dark:hover:bg-green-700 p-2.5 sm:p-2 w-full sm:w-auto text-sm sm:text-base"
                            label="Güncelle"
                            icon="pi pi-check"
                            loading={passwordFormik.isSubmitting}
                            onClick={passwordFormik.handleSubmit}
                        />
                    </div>
                }
            >
                <form onSubmit={passwordFormik.handleSubmit} className="p-fluid">
                    <div className="flex-col space-y-4 sm:space-y-6">
                        <div className="mb-4">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Şifre gereksinimleri:</p>
                            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                                <li>• En az 8 karakter</li>
                                <li>• En az bir küçük harf</li>
                                <li>• En az bir büyük harf</li>
                                <li>• En az bir rakam</li>
                                <li>• En az bir özel karakter</li>
                            </ul>
                        </div>

                        <InputText
                            id="currentPassword"
                            name="currentPassword"
                            type="password"
                            showTemplate={false}
                            value={passwordFormik.values.currentPassword}
                            onChange={passwordFormik.handleChange}
                            onBlur={passwordFormik.handleBlur}
                            label="Mevcut Şifre"
                            error={passwordFormik.errors.currentPassword}
                            touched={passwordFormik.touched.currentPassword}
                            required
                        />

                        <InputText
                            id="newPassword"
                            name="newPassword"
                            type="password"
                            value={passwordFormik.values.newPassword}
                            onChange={passwordFormik.handleChange}
                            onBlur={passwordFormik.handleBlur}
                            label="Yeni Şifre"
                            error={passwordFormik.errors.newPassword}
                            touched={passwordFormik.touched.newPassword}
                            required
                        />

                        <InputText
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            showTemplate={false}
                            value={passwordFormik.values.confirmPassword}
                            onChange={passwordFormik.handleChange}
                            onBlur={passwordFormik.handleBlur}
                            label="Yeni Şifrenizi Onaylayın"
                            error={passwordFormik.errors.confirmPassword}
                            touched={passwordFormik.touched.confirmPassword}
                            feedback={false}
                            required
                        />
                    </div>
                </form>
            </Dialog>

            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden">
                <div className="p-3 sm:p-6">
                    <form onSubmit={profileFormik.handleSubmit}>
                        {/* Notification cards - stack on mobile */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 mb-6">
                            <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-3 sm:p-4 rounded">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <i className="pi pi-bell text-yellow-500"></i>
                                    </div>
                                    <div className="ml-3 min-w-0 flex-1">
                                        <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-200 break-words">
                                            Üyelik Sonlanma Tarihi: <span className="font-semibold">{formatDate(userData.membershipEndDate)}</span>
                                        </p>
                                        <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-200 mt-1 break-words">
                                            KVKK Onayı: <span className="font-semibold">{userData.kvkk?.value || "-"}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 p-3 sm:p-4 rounded">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <i className="pi pi-info-circle text-blue-500"></i>
                                    </div>
                                    <div className="ml-3 min-w-0 flex-1">
                                        <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-200 break-words">
                                            Kimlik numarası, isim, soyisim, e-posta, doğum bilgileri gibi bilgiler sadece görüntülenebilir, değiştirilemez.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="my-4">
                            <WarningArea type='warning' message="Lütfen tüm bilgilerin eksiksiz ve doğru şekilde girildiğinden emin olunuz. Kullanıcı kaynaklı hatalardan doğabilecek maddi kayıplardan PayPlus sorumluluk kabul etmemektedir." />
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                            {/* Left Column - Read-only Personal Information */}
                            <div className="space-y-4 sm:space-y-6">
                                <h3 className="text-lg sm:text-xl font-semibold border-b pb-2 mb-4 dark:text-white dark:border-gray-700">
                                    Kişisel Bilgiler
                                </h3>

                                <div className="bg-gray-100 dark:bg-gray-800 p-3 sm:p-5 rounded-lg shadow-lg">
                                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                            <div>
                                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">İsim</p>
                                                <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{userData.name}</p>
                                            </div>

                                            <div>
                                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Soyisim</p>
                                                <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{userData.surname}</p>
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">E-posta</p>
                                            <p className="font-medium text-sm sm:text-lg dark:text-white break-all">{userData.email}</p>
                                        </div>

                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Kullanıcı Adı</p>
                                            <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{userData.username}</p>
                                        </div>

                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">T.C. Kimlik No</p>
                                            <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{userData.identityNumber}</p>
                                        </div>

                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">VKN</p>
                                            <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{userData.vkn || "-"}</p>
                                        </div>

                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Doğum Tarihi</p>
                                            <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{formatDate(userData.birthday)}</p>
                                        </div>

                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Doğum Yeri</p>
                                            <p className="font-medium text-sm sm:text-lg dark:text-white break-words">{userData.countryOfBirth}, {userData.districtOfBirth}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 sm:mt-6">
                                    <Button
                                        type="button"
                                        label="Şifremi Güncelle"
                                        icon="pi pi-lock"
                                        className="w-full rounded-xl shadow-lg cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-700 dark:hover:bg-indigo-800 p-2 sm:p-3 text-sm sm:text-base"
                                        onClick={() => setShowPasswordModal(true)}
                                    />
                                </div>
                            </div>

                            {/* Right Column - Editable Fields */}
                            <div className="space-y-4 sm:space-y-6">
                                <h3 className="text-lg sm:text-xl font-semibold border-b pb-2 mb-4 dark:text-white dark:border-gray-700">
                                    İletişim ve Adres Bilgileri
                                </h3>

                                <div className="mt-2 flex justify-end">
                                    <p className="text-red-600 dark:text-red-400 font-semibold italic text-xs sm:text-sm">* Zorunlu alanları ifade eder.</p>
                                </div>

                                <h4 className="text-base sm:text-lg font-medium dark:text-white mb-2">Telefon Bilgileri</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                    <InputText
                                        id="phoneNumber1"
                                        name="phoneNumber1"
                                        type="phone"
                                        value={profileFormik.values.phoneNumber1}
                                        onChange={profileFormik.handleChange}
                                        clearFunction={() => profileFormik.setFieldValue("phoneNumber1", "")}
                                        onBlur={profileFormik.handleBlur}
                                        label="Telefon Numarası"
                                        error={profileFormik.errors.phoneNumber1}
                                        touched={profileFormik.touched.phoneNumber1}
                                        required
                                    />

                                    <InputText
                                        id="phoneNumber2"
                                        name="phoneNumber2"
                                        type="phone"
                                        value={profileFormik.values.phoneNumber2}
                                        onChange={profileFormik.handleChange}
                                        clearFunction={() => profileFormik.setFieldValue("phoneNumber2", "")}
                                        onBlur={profileFormik.handleBlur}
                                        label="Telefon Numarası - 2"
                                        error={profileFormik.errors.phoneNumber2}
                                        touched={profileFormik.touched.phoneNumber2}
                                    />
                                </div>

                                <h4 className="text-base sm:text-lg font-medium dark:text-white mt-4 mb-2">İkamet Adresi</h4>
                                <StateSelector
                                    formik={profileFormik}
                                    labelIl="İkamet İl"
                                    labelIlce="İkamet İlçe"
                                    ilFieldName="countryOfResidentialAddress"
                                    ilceFieldName="districtOfResidentialAddress"
                                    required
                                />

                                <InputText
                                    camelCase={true}
                                    type="textarea"
                                    id="detailOfResidentialAddress"
                                    name="detailOfResidentialAddress"
                                    value={profileFormik.values.detailOfResidentialAddress}
                                    onChange={profileFormik.handleChange}
                                    clearFunction={() => profileFormik.setFieldValue("detailOfResidentialAddress", "")}
                                    onBlur={profileFormik.handleBlur}
                                    rows={3}
                                    label="Adres Detayı"
                                    error={profileFormik.errors.detailOfResidentialAddress}
                                    touched={profileFormik.touched.detailOfResidentialAddress}
                                    required
                                />

                                <h4 className="text-base sm:text-lg font-medium dark:text-white mt-4 mb-2">İş Adresi</h4>
                                <StateSelector
                                    formik={profileFormik}
                                    labelIl="İş İl"
                                    labelIlce="İş İlçe"
                                    ilFieldName="countryOfWorkAddress"
                                    ilceFieldName="districtOfWorkAddress"
                                    required
                                />
                                <InputText
                                    camelCase={true}
                                    type="textarea"
                                    id="detailOfWorkAddress"
                                    name="detailOfWorkAddress"
                                    value={profileFormik.values.detailOfWorkAddress}
                                    onChange={profileFormik.handleChange}
                                    onBlur={profileFormik.handleBlur}
                                    rows={3}
                                    clearFunction={() => profileFormik.setFieldValue("detailOfWorkAddress", "")}
                                    label="Adres Detayı"
                                    error={profileFormik.errors.detailOfWorkAddress}
                                    touched={profileFormik.touched.detailOfWorkAddress}
                                    required
                                />
                            </div>
                        </div>

                        <div className="mt-6 sm:mt-8 flex justify-end">
                            <Button
                                type="button"
                                className={`rounded-xl shadow-lg cursor-pointer text-white text-sm sm:text-base ${formChanged
                                    ? 'bg-green-600 dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-800'
                                    : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'} p-2 sm:p-3 w-full sm:w-auto`}
                                label="Bilgilerimi Güncelle"
                                icon="pi pi-save"
                                loading={profileFormik.isSubmitting}
                                disabled={!formChanged || profileFormik.isSubmitting}
                                onClick={handleSubmitClick}
                            />
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                :global(.p-float-label) {
                    width: 100%;
                }
                
                :global(.p-float-label input:focus) ~ label,
                :global(.p-float-label input.p-filled) ~ label,
                :global(.p-float-label textarea:focus) ~ label,
                :global(.p-float-label textarea.p-filled) ~ label {
                    top: -0.75rem;
                    font-size: 12px;
                    background-color: #ffffff;
                    padding: 2px 4px;
                    border-radius: 3px;
                }
                
                :global(.dark) .p-float-label input:focus ~ label,
                :global(.dark) .p-float-label input.p-filled ~ label,
                :global(.dark) .p-float-label textarea:focus ~ label,
                :global(.dark) .p-float-label textarea.p-filled ~ label {
                    background-color: #2d3748;
                    color: #60a5fa;
                }
                
                :global(.p-float-label input),
                :global(.p-float-label textarea) {
                    padding-top: 0.75rem !important;
                }

                :global(.p-inputtext) {
                    background-color: transparent;
                }
                
                :global(.dark) .p-inputtext {
                    color: #e2e8f0;
                    border-color: #4a5568;
                    background-color: #2d3748;
                }
                
                :global(.dark) .p-float-label > label {
                    color: #9ca3af;
                }
                
                :global(.dark) .p-inputtextarea {
                    color: #e2e8f0;
                    border-color: #4a5568;
                    background-color: #2d3748;
                }
                
                :global(.dark) .p-dialog .p-dialog-header {
                    background-color: #1f2937;
                    color: #e5e7eb;
                    border-color: #4b5563;
                }
                
                :global(.dark) .p-dialog .p-dialog-content {
                    background-color: #1f2937;
                    color: #e5e7eb;
                    border-color: #4b5563;
                }
                
                :global(.dark) .p-dialog .p-dialog-footer {
                    background-color: #1f2937;
                    color: #e5e7eb;
                    border-color: #4b5563;
                }
                
                :global(.dark) .p-error {
                    color: #f87171;
                }

                /* Mobile specific improvements */
                @media (max-width: 640px) {
                    :global(.p-dialog-content) {
                        padding: 1rem !important;
                    }
                    
                    :global(.p-dialog-footer) {
                        padding: 0.75rem 1rem !important;
                    }
                    
                    :global(.p-button) {
                        font-size: 0.875rem !important;
                        padding: 0.5rem 1rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Profile;